import { useCallback, useEffect, useState } from 'react'
import { Coordinates, FetchStoresError, StoreWithDistance } from './types'
import { geocode } from '../services/google-maps-service'
import { mapSitesToStores } from '../utils/map-sites-to-stores'
import { getDistance } from 'geolib'
import { getSites } from '../services/cinch-servicing'
import { datadogRum } from '@datadog/browser-rum'
import { round } from 'lodash'

const METERS_TO_MILES_CONVERSION = 0.000_621_371

export const useFetchAllStoresAndCalculateDistance = (postcode?: string) => {
  const [storesWithDistance, setStoresWithDistance] = useState<
    StoreWithDistance[]
  >([])
  const [customerCoordinates, setCustomerCoordinates] = useState<
    Coordinates | undefined
  >()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FetchStoresError | null>(null)

  useEffect(() => {
    if (postcode) {
      fetchAllStoresAndCalculateDistances(postcode)
    }
  }, [postcode])

  const fetchAllStoresAndCalculateDistances = useCallback(
    async (postcode: string) => {
      setLoading(true)
      setError(null)
      try {
        const customerCoordinates = await geocode(postcode)
        if (!customerCoordinates) {
          datadogRum.addError(new Error('Cinch Servicing: Postcode Error'), {
            cinchServicing: true,
            postcode,
          })
          setError('Postcode')
        } else {
          const sites = await getSites()
          if (!sites) {
            throw new Error('Error retrieving stores')
          }
          const stores = mapSitesToStores(sites)

          const updatedStores = stores
            .map((store) => {
              const distance = calculateStoreDistance(
                customerCoordinates,
                store.coordinates,
              )
              return {
                store,
                distance,
              }
            })
            .sort((a, b) => a.distance - b.distance)
            .map((s, index) => ({ ...s, index }))

          const closestStore = updatedStores[0]
          const workshopPlusStore = updatedStores.find(
            (store) => store.store.canBookAppointment === true,
          )

          const hasWorkshopPlusStore = workshopPlusStore !== undefined
          const shouldPrioritize =
            hasWorkshopPlusStore &&
            shouldPrioritizeWorkshopPlus(workshopPlusStore, closestStore)

          const finalStores = shouldPrioritize
            ? prioritizeWorkshopPlusStore(updatedStores, workshopPlusStore)
            : updatedStores

          setStoresWithDistance(finalStores)

          setCustomerCoordinates({
            latitude: customerCoordinates.lat,
            longitude: customerCoordinates.lng,
          })
        }
      } catch {
        datadogRum.addError(new Error('Cinch Servicing: Stores Error'), {
          cinchServicing: true,
        })
        setError('Stores')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return {
    storesWithDistance,
    customerCoordinates,
    loading,
    error,
  }
}

const calculateStoreDistance = (
  customerCoordinates: { lat: number; lng: number },
  nearestCollectionSite: {
    longitude: number
    latitude: number
  },
): number => {
  const { latitude, longitude } = nearestCollectionSite
  return Math.floor(
    getDistance(
      { latitude: customerCoordinates.lat, longitude: customerCoordinates.lng },
      { latitude, longitude },
    ),
  )
}

const toMiles = (meters: number) =>
  round(meters * METERS_TO_MILES_CONVERSION, 1)

const shouldPrioritizeWorkshopPlus = (
  workshopPlusStore: StoreWithDistance,
  closestStore: StoreWithDistance,
): boolean => {
  const workshopPlusDistanceInMiles = toMiles(workshopPlusStore.distance)
  const closestStoreDistanceInMiles = toMiles(closestStore.distance)
  const distanceDifference =
    workshopPlusDistanceInMiles - closestStoreDistanceInMiles

  return distanceDifference < 1
}

const prioritizeWorkshopPlusStore = (
  stores: StoreWithDistance[],
  workshopPlusStore: StoreWithDistance,
): StoreWithDistance[] => {
  const otherStores = stores.filter(
    (store) => store.store.id !== workshopPlusStore.store.id,
  )
  return [workshopPlusStore, ...otherStores]
}
